import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { ALIGN_OPTIONS, lineHeightField, marginFields, mobileWidthPxField, visibilityField, weightField, widthPxField, } from '../_shared/blockFields.js';
import { AUTO_BODY_LEADING, marginStyle, num, str, visibilityCss, richtextProseCss, readLineHeight, readWeight, widthCss, } from '../_shared/blockRender.js';
import { animationFields, blinkTextCss, readBlinkSpeed, readTextAnimation, textAnimationAttrs, useTextAnimation, } from '../_shared/textAnimation.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { blockGradientCss, blockGradientField } from '../../sections/_shared/textColor.js';
/**
 * Shrine generic `text` block — config-by-config. A standalone rich-text / paragraph block bound to its
 * OWN `text` richtext setting (NOT product data). Reproduces Shrine's authoring surface, grouped as Shrine
 * groups it:
 *  - visibility ("Display on") + the richtext body.
 *  - alignment (Desktop) + mobile_alignment — independent, responsive at Shrine's 750px breakpoint via BOTH
 *    `@media` (live viewport) and `@container aqx-page` (builder device-preview), like the title block.
 *  - OURS: width per device in PIXELS (`desktopWidthPx` + `mobileWidthPx`, unset = full width; 24/09/2026,
 *    « pas par taille qui change variable de pourcentage »). The `%` pair it replaced (`desktopWidth` +
 *    `mobileWidth`, 22-24/09) is neither shown nor read on this block any more. The two devices are
 *    independent and each is positioned by ITS OWN alignment; see `widthCss()`.
 *  - text_size (10–24px, def 14).
 *  - enable_custom_color + custom_text_color (explicit hex override of the scheme text colour).
 *  - "Highlight 1" — styles BOLD words (`<strong>`/`<b>`): none / solid-color / gradient-color (clipped to
 *    text) / bg-color (a padded pill with radius + optional border), + a "remove bold weight" toggle.
 *  - "Highlight 2" — the SAME for ITALIC words (`<em>`/`<i>`), + a "remove italic style" toggle.
 *  - "Margins" (top/bottom, def 15/15).
 *
 * Shrine maps `color_background` → our `gradient` field type and `color` → our `color` field (stored as a
 * hex string here, matching Shrine's raw colour pickers — these are deliberate one-off colours, not scheme
 * selects, so they are plain string settings read with `str()`).
 *
 * "Animations" — text_animation (none / split / shiny / blink / blur / typing). ALL are wired: "shiny" is a
 * continuous pure-CSS shimmer (gradient sweep clipped to the glyphs, `shinySpeed`); split / blur / typing
 * are scroll-triggered reveal effects driven by the shared {@link useTextAnimation} runtime (per-word/char
 * DOM splitting, blur-in, typewriter — IntersectionObserver, prefers-reduced-motion aware). SSR renders the
 * static text; the runtime applies the effect once on the client.
 *
 * DEFERRED (Shrine settings intentionally NOT reproduced):
 *  - The orphaned highlight / highlighter knobs (highlight_bold_only / highlight_color / highlighter_*):
 *    dead schema in Shrine — their visible_if references text_animation values (highlight-swipe/fade-up/
 *    underline/strike) that do NOT exist in the option list, so they are unreachable even in Shrine.
 */
export const textBlockSchema = {
    type: 'text',
    name: 'Text',
    fields: [
        visibilityField,
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            default: {
                format: 'html',
                html: "<p>Utilisez la grille flexible pour composer votre mise en page. Chaque bloc se place dans n'importe quelle colonne.</p>",
            },
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Desktop alignment',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            min: 10,
            max: 24,
            step: 1,
            default: 14,
            unit: 'px',
        },
        // Beside the size, because it is the size that makes it necessary: the theme's 1.8 is a leading for
        // 14px copy, and it opens a hole between the lines as soon as the slider above is pushed up.
        lineHeightField(1.8, 'textSize'),
        weightField,
        // Width in PIXELS (founder, 24/09/2026): « configurable par les pixel et pas par taille qui change
        // variable de pourcentage ». Unset = full width. The `%` slider it replaced is GONE from this block —
        // « remove the desktop width pourcentage, here they are contradicting the pixel and pourcentage » —
        // and a stored `desktopWidth`/`mobileWidth` is not read any more either (`legacyPercent: false`): a
        // value no control shows must not paint.
        widthPxField,
        // ── Colors — the block's text colour AND an optional filled/rounded background panel, together ──
        {
            id: 'enableCustomColor',
            type: 'checkbox',
            label: 'Enable custom text color',
            group: 'Colors',
            default: false,
        },
        {
            id: 'customTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'enableCustomColor', equals: true },
        },
        // The gradient, beside the colour and behind the same switch (2026-09-20). A rich-text paragraph is a
        // BLOCK, so the section's "Text colors" group is not open when the seller is styling this one.
        blockGradientField('Colors'),
        {
            id: 'enableBackground',
            type: 'checkbox',
            label: 'Enable background color',
            group: 'Colors',
            default: false,
        },
        {
            id: 'backgroundColor',
            type: 'color',
            label: 'Background color',
            group: 'Colors',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'enableBackground', equals: true },
        },
        {
            id: 'backgroundPadding',
            type: 'range',
            label: 'Padding',
            group: 'Colors',
            min: 0,
            max: 48,
            step: 2,
            default: 16,
            unit: 'px',
            visibleWhen: { field: 'enableBackground', equals: true },
        },
        {
            id: 'backgroundRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Colors',
            min: 0,
            max: 40,
            step: 2,
            default: 12,
            unit: 'px',
            visibleWhen: { field: 'enableBackground', equals: true },
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile alignment',
            group: 'Mobile',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        // The phone twin of `widthPxField` above: the two devices narrow independently (22/09/2026).
        mobileWidthPxField,
        // ── Highlight 1 — styles BOLD words ─────────────────────────────────────────
        {
            id: 'boldTextStyle',
            type: 'select',
            label: 'Bold text style',
            group: 'Highlight 1',
            info: 'Put words in BOLD to highlight them with this style. Keep it as "None" to use the default bold style.',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid-color', label: 'Solid color' },
                { value: 'gradient-color', label: 'Gradient color' },
                { value: 'bg-color', label: 'Background color' },
            ],
        },
        {
            id: 'boldSolidColor',
            type: 'color',
            label: 'Text color',
            group: 'Highlight 1',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'boldTextStyle', equals: 'solid-color' },
        },
        {
            id: 'boldGradientColor',
            type: 'gradient',
            label: 'Text gradient',
            group: 'Highlight 1',
            default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
            visibleWhen: { field: 'boldTextStyle', equals: 'gradient-color' },
        },
        {
            id: 'boldBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Highlight 1',
            default: { ref: 'custom', value: '#FFD700' },
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Highlight 1',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgPaddingInline',
            type: 'range',
            label: 'Horizontal padding',
            group: 'Highlight 1',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.7,
            unit: 'em',
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgPaddingBlock',
            type: 'range',
            label: 'Vertical padding',
            group: 'Highlight 1',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.2,
            unit: 'em',
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgBorderRadius',
            type: 'range',
            label: 'Border radius',
            group: 'Highlight 1',
            min: 0,
            max: 40,
            step: 2,
            default: 8,
            unit: 'px',
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgBorderWidth',
            type: 'range',
            label: 'Border thickness',
            group: 'Highlight 1',
            min: 0,
            max: 6,
            step: 1,
            default: 0,
            unit: 'px',
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgBorderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Highlight 1',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldBgBorderColor',
            type: 'color',
            label: 'Border color',
            group: 'Highlight 1',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'boldTextStyle', equals: 'bg-color' },
        },
        {
            id: 'boldRemoveWeight',
            type: 'checkbox',
            label: 'Remove bold font weight',
            group: 'Highlight 1',
            info: 'Removes the bold styling but keeps the custom color/background.',
            default: false,
            visibleWhen: { field: 'boldTextStyle', notEquals: 'none' },
        },
        // ── Highlight 2 — styles ITALIC words ───────────────────────────────────────
        {
            id: 'italicTextStyle',
            type: 'select',
            label: 'Italic text style',
            group: 'Highlight 2',
            info: 'Put words in ITALIC to highlight them with this style. Keep it as "None" to use the default italic style.',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid-color', label: 'Solid color' },
                { value: 'gradient-color', label: 'Gradient color' },
                { value: 'bg-color', label: 'Background color' },
            ],
        },
        {
            id: 'italicSolidColor',
            type: 'color',
            label: 'Text color',
            group: 'Highlight 2',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'italicTextStyle', equals: 'solid-color' },
        },
        {
            id: 'italicGradientColor',
            type: 'gradient',
            label: 'Text gradient',
            group: 'Highlight 2',
            default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
            visibleWhen: { field: 'italicTextStyle', equals: 'gradient-color' },
        },
        {
            id: 'italicBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Highlight 2',
            default: { ref: 'custom', value: '#FFD700' },
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Highlight 2',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgPaddingInline',
            type: 'range',
            label: 'Horizontal padding',
            group: 'Highlight 2',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.7,
            unit: 'em',
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgPaddingBlock',
            type: 'range',
            label: 'Vertical padding',
            group: 'Highlight 2',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.2,
            unit: 'em',
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgBorderRadius',
            type: 'range',
            label: 'Border radius',
            group: 'Highlight 2',
            min: 0,
            max: 40,
            step: 2,
            default: 8,
            unit: 'px',
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgBorderWidth',
            type: 'range',
            label: 'Border thickness',
            group: 'Highlight 2',
            min: 0,
            max: 6,
            step: 1,
            default: 0,
            unit: 'px',
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgBorderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Highlight 2',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicBgBorderColor',
            type: 'color',
            label: 'Border color',
            group: 'Highlight 2',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'italicTextStyle', equals: 'bg-color' },
        },
        {
            id: 'italicRemoveStyle',
            type: 'checkbox',
            label: 'Remove italic font style',
            group: 'Highlight 2',
            info: 'Removes the italic styling but keeps the custom color/background.',
            default: false,
            visibleWhen: { field: 'italicTextStyle', notEquals: 'none' },
        },
        // ── Animations (Shrine `text_animation`) — shared runtime: shiny (CSS), split / blur / typing (JS
        //    reveal on scroll via the useTextAnimation hook). ───────────────────────────────────────────────
        ...animationFields(),
        ...marginFields(15, 15),
    ],
};
/** A stored `color` setting is a ColorValue; read its hex (custom) or fall back to a literal. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        // 0019 §4a: the THEME branch. Without it a { ref:'theme', token } silently fell through to the
        // frozen fallback, so a seller re-theming their store changed nothing here (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    // Tolerate a plain hex string too (defensive — older/raw documents).
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** Build the CSS declarations for one highlight style ("Highlight 1" → bold, "Highlight 2" → italic). */
function highlightCss(bs, prefix, 
/** Selector tail for the matched inline tags (e.g. ` b, ${cls} strong`). */
selector, 
/** Whether the strip-style toggle removes the native bold-weight / italic-style. */
removeNative, 
/** The native style property + value this highlight kind owns (weight for bold, style for italic). */
nativeProp) {
    const style = str(bs[`${prefix}TextStyle`], 'none');
    if (style === 'none')
        return '';
    const sel = selector;
    // When asked, drop the native bold/italic styling but keep the colour/background.
    const stripNative = nativeProp === 'fontWeight'
        ? removeNative
            ? 'font-weight:inherit;'
            : ''
        : removeNative
            ? 'font-style:normal;'
            : '';
    if (style === 'solid-color') {
        // Fallback = the schema default (theme accent), not Shrine's demo blue — see blocks/heading highlightCss.
        const color = colorHex(bs[`${prefix}SolidColor`], 'var(--aqx-theme-accent)');
        return `${sel}{color:${color};${stripNative}}`;
    }
    if (style === 'gradient-color') {
        const grad = str(bs[`${prefix}GradientColor`], 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)');
        // Clip the gradient to the glyphs (Shrine's gradient-text technique).
        return (`${sel}{background:${grad};-webkit-background-clip:text;background-clip:text;` +
            `-webkit-text-fill-color:transparent;color:transparent;${stripNative}}`);
    }
    // bg-color → a padded pill with optional border.
    const bg = colorHex(bs[`${prefix}BgColor`], '#FFD700');
    const txt = colorHex(bs[`${prefix}BgTextColor`], prefix === 'bold' ? '#000000' : '#121212');
    const padInline = num(bs[`${prefix}BgPaddingInline`], 0.7);
    const padBlock = num(bs[`${prefix}BgPaddingBlock`], 0.2);
    const radius = num(bs[`${prefix}BgBorderRadius`], 8);
    const borderWidth = num(bs[`${prefix}BgBorderWidth`], 0);
    const borderStyleVal = str(bs[`${prefix}BgBorderStyle`], 'solid');
    const borderColor = colorHex(bs[`${prefix}BgBorderColor`], '#121212');
    const border = borderWidth > 0 ? `border:${borderWidth}px ${borderStyleVal} ${borderColor};` : '';
    return (`${sel}{background:${bg};color:${txt};` +
        `padding:${padBlock}em ${padInline}em;border-radius:${radius}px;` +
        `box-decoration-break:clone;-webkit-box-decoration-break:clone;${border}${stripNative}}`);
}
/**
 * Generic rich-text block. The body is the block's OWN sanitized `text` richtext; size + alignment are
 * responsive (desktop base, mobile override at 750px in both `@media` and `@container`); custom colour and
 * the two highlight styles are injected as scoped rules targeting `<strong>`/`<b>` and `<em>`/`<i>`.
 */
const TextBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-tx-${block.id}`;
    // Reveal animation (split / blur / typing) on scroll-into-view; shiny is handled by the scoped CSS below.
    const textAnim = readTextAnimation(bs);
    const animRef = useTextAnimation(textAnim);
    const rt = bs.text;
    const html = sanitizeRichtextHtml(rt?.format === 'html' ? rt.html : '');
    const size = num(bs.textSize, 14);
    const align = str(bs.alignment, 'left');
    const mAlign = str(bs.mobileAlignment, 'center');
    // Shared "Font weight" — applied to the base text rule only; unset ('') emits nothing (zero migration).
    const weight = readWeight(bs);
    const textColor = bs.enableCustomColor === true
        ? colorHex(bs.customTextColor, '#121212')
        : 'var(--aqx-scheme-text)';
    // Mobile-first base = mobile alignment; desktop alignment overrides at ≥750px (matching the title block's
    // responsive pattern). Font size is uniform across breakpoints in Shrine (single text_size).
    const base = `.${cls}{font-size:${size}px;text-align:${mAlign}${weight ? `;font-weight:${weight}` : ''}}`;
    const desktopRule = `.${cls}{text-align:${align}}`;
    const boldCss = highlightCss(bs, 'bold', `.${cls} strong,.${cls} b`, bs.boldRemoveWeight === true, 'fontWeight');
    const italicCss = highlightCss(bs, 'italic', `.${cls} em,.${cls} i`, bs.italicRemoveStyle === true, 'fontStyle');
    // "Shiny" text animation — a continuous CSS shimmer sweeping a light band across the glyphs. Pure CSS
    // (gradient clipped to text + a keyframed background-position), no JS / no scroll observer. `shinySpeed`
    // is the sweep duration in seconds. The base text colour shows through the dark gradient stops, so it
    // honours custom/scheme colour. `prefers-reduced-motion` pins it static.
    const isShiny = str(bs.textAnimation, 'none') === 'shiny';
    const shinySpeed = num(bs.shinySpeed, 5);
    const shinyCss = isShiny
        ? `@keyframes aqx-tx-shine-${block.id}{to{background-position:-200% 0}}` +
            `.${cls}{` +
            `background:linear-gradient(110deg,${textColor} 0%,${textColor} 40%,rgba(255,255,255,0.85) 50%,${textColor} 60%,${textColor} 100%);` +
            `background-size:200% 100%;` +
            `-webkit-background-clip:text;background-clip:text;` +
            `-webkit-text-fill-color:transparent;color:transparent;` +
            `animation:aqx-tx-shine-${block.id} ${shinySpeed}s linear infinite}` +
            `@media(prefers-reduced-motion:reduce){.${cls}{animation:none}}`
        : '';
    // "Blink" — opacity only: colour, gradient, bold/italic highlights all stay as the seller set them.
    const blinkCss = textAnim.type === 'blink'
        ? blinkTextCss(`.${cls}`, `aqx-tx-blink-${block.id}`, readBlinkSpeed(bs))
        : '';
    const css = base +
        // Desktop override via `@container aqx-page` ONLY (the aqx-page container is always present via
        // PageRenderer). A paired @media(min-width) would fire off the builder's wide window in a narrow preview.
        `@container aqx-page (min-width:750px){${desktopRule}}` +
        // Optional width per device (px only), each positioned by ITS OWN alignment; '' while unset.
        widthCss(cls, bs, align, mAlign, { legacyPercent: false }) +
        // Re-establish list markers / heading sizes / underline that Tailwind Preflight strips from the
        // injected richtext (emitted BEFORE the highlight rules so a bold/italic highlight still wins).
        richtextProseCss(cls) +
        shinyCss +
        blinkCss +
        // Never against "shiny": that animation already clips a gradient of its own to these glyphs.
        (isShiny ? '' : blockGradientCss(`.${cls}`, bs)) +
        boldCss +
        italicCss +
        visibilityCss(cls, bs.visibility);
    // Optional background panel behind the whole block (a filled, rounded, padded card).
    const bgStyle = bs.enableBackground === true
        ? {
            // Fallback follows the theme too — a frozen lavender here is what made an un-set banner render
            // off-palette (the "why is this button pink" report) even after the document was tokenised.
            backgroundColor: colorHex(bs.backgroundColor, 'var(--aqx-theme-accent-2)'),
            padding: `${num(bs.backgroundPadding, 16)}px`,
            borderRadius: `${num(bs.backgroundRadius, 12)}px`,
        }
        : {};
    const style = {
        // When shiny is active the colour is owned by the scoped stylesheet (gradient clipped to text +
        // color:transparent); an inline `color` would override it, so omit it in that case.
        ...(isShiny ? {} : { color: textColor }),
        // 0019 §5a: le ratio du thème reste la source unique — mais appliqué par la courbe affine
        // `AUTO_BODY_LEADING`, qui le rend tel quel à 14 px et le resserre au-dessus (20/09/2026). Le réglage
        // « Interligne » du bloc, quand elle l'a pris, passe devant.
        lineHeight: readLineHeight(bs, AUTO_BODY_LEADING),
        fontFamily: 'var(--aqx-theme-font)',
        wordBreak: 'break-word',
        ...bgStyle,
        ...marginStyle(bs, 15, 15),
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { ref: animRef, className: cls, style: style, ...textAnimationAttrs(textAnim), dangerouslySetInnerHTML: { __html: html } })] }));
};
export const textBlock = {
    type: 'text',
    schema: textBlockSchema,
    Component: TextBlock,
    meta: { label: 'Text' },
};
//# sourceMappingURL=index.js.map